import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PortalProvider } from './portal/PortalContext';
import { Toaster } from './components/ui/sonner';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import POS from './pages/POS';
import Products from './pages/Products';
import Subscriptions from './pages/Subscriptions';
import Debts from './pages/Debts';
import Reports from './pages/Reports';
import AdvancedReports from './pages/AdvancedReports';
import Users from './pages/Users';
import Members from './pages/Members';
import PortalLogin from './portal/PortalLogin';
import PortalSetupPassword from './portal/PortalSetupPassword';
import PortalDashboard from './portal/PortalDashboard';

const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      <main className="lg:ml-64 p-4 pt-16 lg:pt-4 lg:p-8 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

const AdminRoute = () => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
};

function App() {
  return (
    <AuthProvider>
      <PortalProvider>
        <BrowserRouter>
          <div className="noise-overlay" />
          <Routes>
            {/* Portal del Socio (user-facing, en la raíz) */}
            <Route path="/" element={<PortalLogin />} />
            <Route path="/setup-password" element={<PortalSetupPassword />} />
            <Route path="/dashboard" element={<PortalDashboard />} />

            {/* Legacy /portal/* redirects para retrocompatibilidad */}
            <Route path="/portal" element={<Navigate to="/dashboard" replace />} />
            <Route path="/portal/login" element={<Navigate to="/" replace />} />
            <Route path="/portal/setup-password" element={<PortalSetupPassword />} />

            {/* Admin area bajo /admin/* */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin/pos" element={<POS />} />
              <Route path="/admin/products" element={<Products />} />
              <Route path="/admin/subscriptions" element={<Subscriptions />} />
              <Route path="/admin/debts" element={<Debts />} />
              <Route path="/admin/reports" element={<Reports />} />
              <Route path="/admin/advanced-reports" element={<AdvancedReports />} />
              <Route path="/admin/members" element={<Members />} />

              {/* Legacy admin aliases */}
              <Route path="/pos" element={<Navigate to="/admin/pos" replace />} />
              <Route path="/products" element={<Navigate to="/admin/products" replace />} />
              <Route path="/subscriptions" element={<Navigate to="/admin/subscriptions" replace />} />
              <Route path="/debts" element={<Navigate to="/admin/debts" replace />} />
              <Route path="/reports" element={<Navigate to="/admin/reports" replace />} />
              <Route path="/advanced-reports" element={<Navigate to="/admin/advanced-reports" replace />} />
              <Route path="/members" element={<Navigate to="/admin/members" replace />} />

              <Route element={<AdminRoute />}>
                <Route path="/admin/users" element={<Users />} />
                <Route path="/users" element={<Navigate to="/admin/users" replace />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#0A0A0A',
                border: '1px solid #27272A',
                color: '#FAFAFA'
              }
            }}
          />
        </BrowserRouter>
      </PortalProvider>
    </AuthProvider>
  );
}

export default App;
